import { Tool } from 'models/chatbot';

export interface CommandArgument {
  name: string;
  description: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
}

export interface Command {
  name: string;
  description: string;
  arguments?: CommandArgument[];
}

export const convertToolsToCommands = (tools: Tool[]): Command[] => {
  return tools.map((tool) => {
    const func = tool.function;
    const command: Command = {
      name: func.name,
      description: func.description,
    };

    if (func.parameters && func.parameters.properties) {
      const properties = func.parameters.properties;
      const requiredParams = func.parameters.required || [];

      command.arguments = Object.keys(properties).map((paramName) => {
        const param = properties[paramName];
        return {
          name: paramName,
          description: param.description,
          placeholder: paramName,
          required: requiredParams.includes(paramName),
          type: param.type,
        };
      });
    }

    return command;
  });
};

export const findMatchingCommands = (input: string, commands: Command[]): Command[] => {
  if (!input.startsWith('/')) return [];

  const searchTerm = input.substring(1).toLowerCase();
  return commands.filter((cmd) => cmd.name.toLowerCase().startsWith(searchTerm));
};

export const getCommandSuggestion = (commandName: string, commands: Command[]): string => {
  const command = commands.find((cmd) => cmd.name === commandName);
  if (!command) return '';

  let suggestion = `/${command.name}`;

  if (command.arguments) {
    command.arguments.forEach((arg) => {
      const argType = arg.type === 'number' ? 'number' : 'text';
      if (arg.required) {
        suggestion += ` <${arg.placeholder || arg.name} (${argType})>`;
      } else {
        suggestion += ` [${arg.placeholder || arg.name} (${argType})]`;
      }
    });
  }

  return suggestion;
};

export const getNextArgumentPlaceholder = (
  commandName: string,
  currentArgs: string[],
  commands: Command[],
): string | null => {
  const command = commands.find((cmd) => cmd.name === commandName);
  if (!command || !command.arguments) return null;

  const nextArgIndex = currentArgs.length;
  if (nextArgIndex >= command.arguments.length) return null;

  const nextArg = command.arguments[nextArgIndex];
  return nextArg.placeholder || nextArg.name;
};

export const getNextArgumentPlaceholderWithType = (
  commandName: string,
  currentArgs: string[],
  commands: Command[],
): string | null => {
  const command = commands.find((cmd) => cmd.name === commandName);
  if (!command || !command.arguments) return null;

  const nextArgIndex = currentArgs.length;
  if (nextArgIndex >= command.arguments.length) return null;

  const nextArg = command.arguments[nextArgIndex];
  const argType = nextArg.type || 'string';
  return `${nextArg.placeholder || nextArg.name} (${argType === 'number' ? 'number' : 'text'})`;
};
